import React from 'react';
import Heading from './Heading.jsx';
import WidthWrapper from './WidthWrapper.jsx';
import Tags from './Tags.jsx';
import pin from '../images/map-pin.svg';
import globe from '../images/globe.svg';
import { PrismicRichText } from '@prismicio/react';
import styled from 'styled-components';
import { GatsbyImage } from 'gatsby-plugin-image';
import Video from './Video.jsx';

const StyledGatedCaseStudyHero = styled.section`
  .hero {
    object-fit: cover;
    height: 33.5rem;
    width: 100%;
  }

  .case-study {
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 50rem;
    width: 100%;
    padding: 5rem 0;
  }

  .info {
    max-width: 33.5rem;
    padding-right: 1rem;
  }

  p {
    text-align: left;
    padding-bottom: 1.5rem;
  }

  a.ext {
    text-transform: uppercase;
    text-decoration: none;
    color: var(--primary);
    border-bottom: 2px solid var(--primary);
  }

  .logo-links {
    display: flex;
    gap: 4rem;
  }

  .website,
  .location {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  .tags {
    margin-top: -1rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 820px) {
    .case-study {
      flex-direction: column;
      align-items: center;
      gap: 3rem;
    }
  }

  @media only screen and (max-width: 525px) {
    padding: 0 0 6.25rem 0;

    .width-wrapper {
      gap: 2rem;
    }

    .tags {
      margin-top: 0;
      margin-bottom: 0.75rem;
    }

    .hero {
      object-fit: contain;
      height: auto;
      width: 100%;
    }
  }
`;

const StyledNonGatedCaseStudyHero = styled.section`
  padding: 0 0 4.5rem 0;

  .hero {
    object-fit: cover;
    height: 33.5rem;
    width: 100%;
  }

  .width-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    align-items: center;
    max-width: 56rem;
  }

  p {
    text-align: center;
  }

  a.ext {
    text-transform: uppercase;
    text-decoration: none;
    color: var(--primary);
    border-bottom: 2px solid var(--primary);
  }

  .logo-links {
    display: flex;
    gap: 4rem;
  }

  .website,
  .location {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  .tags {
    margin-top: -1rem;
    margin-bottom: 1.25rem;
  }

  @media only screen and (max-width: 525px) {
    padding: 0 0 6.25rem 0;

    .width-wrapper {
      gap: 2rem;
    }

    .tags {
      margin-top: 0;
      margin-bottom: 0.75rem;
    }

    .hero {
      object-fit: contain;
      height: auto;
      width: 100%;
    }
  }
`;

const CaseStudyHero = ({
  headingText,
  contentSummary,
  websiteLink,
  locationText,
  locationLink,
  tags,
  heroImage,
  video,
  caseStudy,
  isGated,
}) => {
  return isGated ? (
    <StyledGatedCaseStudyHero>
      {(video || heroImage) &&
        (video ? (
          <Video src={video} title={headingText} />
        ) : (
          <GatsbyImage
            image={heroImage}
            className="hero"
            alt={headingText}
            blurDataURL={heroImage.placeholder?.fallback}
          />
        ))}
      <WidthWrapper>
        <div className="case-study">
          <div className="info">
            <Heading headingLevel={2} headingText={headingText} leftAligned browText="Case Study" />
            {tags && <Tags tags={tags} />}
            <PrismicRichText
              field={contentSummary.richText}
              components={{
                paragraph: ({ children }) => <p className="body-l-regular">{children}</p>,
              }}
            />
            <div className="logo-links body-l-semibold">
              {websiteLink && (
                <div className="website">
                  <img src={globe} alt="globe" />
                  <a href={websiteLink} target="_blank" rel="noreferrer" className="ext">
                    Website
                  </a>
                </div>
              )}
              {locationLink && (
                <div className="location">
                  <img src={pin} alt="location" />
                  <a href={locationLink} target="_blank" rel="noreferrer" className="ext">
                    {locationText}
                  </a>
                </div>
              )}
            </div>
          </div>
          <iframe
            style={{
              border: '1px solid black',
              height: '42rem',
              width: '29rem',
              maxWidth: '29rem',
            }}
            title="gated-case-study"
            className="gated-form-iframe"
            src={caseStudy.gated_iframe_link?.url}
            width="71.5rem"
            height="100%"
            type="text/html"
            frameborder="0"
            allowTransparency="true"
          />
        </div>
      </WidthWrapper>
    </StyledGatedCaseStudyHero>
  ) : (
    <StyledNonGatedCaseStudyHero>
      {(video || heroImage) &&
        (video ? (
          <Video src={video} title={headingText} />
        ) : (
          <GatsbyImage
            image={heroImage}
            className="hero"
            alt={headingText}
            blurDataURL={heroImage.placeholder?.fallback}
          />
        ))}
      <WidthWrapper>
        <Heading headingLevel={2} headingText={headingText} browText="Case Study" />
        {tags && <Tags tags={tags} />}

        <PrismicRichText
          field={contentSummary.richText}
          components={{
            paragraph: ({ children }) => <p className="body-l-regular">{children}</p>,
          }}
        />
        <div className="logo-links body-l-semibold">
          {websiteLink && (
            <div className="website">
              <img src={globe} alt="globe" />
              <a href={websiteLink} target="_blank" rel="noreferrer" className="ext">
                Website
              </a>
            </div>
          )}
          {locationLink && (
            <div className="location">
              <img src={pin} alt="location" />
              <a href={locationLink} target="_blank" rel="noreferrer" className="ext">
                {locationText}
              </a>
            </div>
          )}
        </div>
      </WidthWrapper>
    </StyledNonGatedCaseStudyHero>
  );
};

export default CaseStudyHero;
